import { type FC, useMemo } from 'react';
import type { DisplayType } from '../types';
import {
  HistoricalYearsTooltip,
  type SalesSummaryProperties,
  SummaryHeader,
  SummaryTile,
  useImportMonth,
} from './index';

export const SalesSummary: FC<SalesSummaryProperties> = ({
  lastImported,
  thisYearSales,
  thisYearEstimates,
  historicalYears,
}) => {
  const displayType: DisplayType = 'Sales';
  const thisYear = useMemo(() => thisYearSales.year, [thisYearSales]);
  const lastYear = useMemo(() => thisYear - 1, [thisYear]);
  const lastYearSales = useMemo(() => historicalYears[0], [historicalYears]);
  const { index, monthName, isMtdCurrent } = useImportMonth(lastImported);

  const historicalTooltipContent = useMemo(() => {
    if (historicalYears.length === 0) {
      return undefined;
    }

    return <HistoricalYearsTooltip historicalYears={historicalYears} displayType={displayType} />;
  }, [historicalYears]);

  return (
    <div className="grid grid-flow-row">
      <SummaryHeader>{monthName}</SummaryHeader>
      <div className="grid grid-flow-col">
        <SummaryTile
          title={`${lastYear} MTD`}
          current={lastYearSales?.totals_to_date_month.sales}
          timespan="lastYear"
          format="currency"
        />
        <SummaryTile
          title={`${lastYear} Total`}
          current={lastYearSales?.monthly[index].sales}
          timespan="lastYear"
          format="currency"
        />
        <SummaryTile
          title={`${thisYear} MTD`}
          current={isMtdCurrent ? thisYearSales.monthly[index].sales : undefined}
          previous={isMtdCurrent ? lastYearSales?.totals_to_date_month.sales : undefined}
          timespan="thisYear"
          format="currency"
        />
        <SummaryTile
          title={`${thisYear} Total ~`}
          current={thisYearEstimates.monthly[index].sales}
          timespan="thisYearEstimates"
          format="currency"
        />
      </div>
      <SummaryHeader>Year</SummaryHeader>
      <div className="grid grid-flow-col">
        <SummaryTile
          title={`${lastYear} YTD`}
          current={lastYearSales?.totals_to_date.sales}
          timespan="lastYear"
          format="currency"
        />
        <SummaryTile
          title={`${lastYear} Total`}
          current={lastYearSales?.totals.sales}
          timespan="lastYear"
          format="currency"
        />
        <SummaryTile
          title={`${thisYear} YTD`}
          current={thisYearSales.totals_to_date.sales}
          previous={lastYearSales?.totals_to_date.sales}
          timespan="thisYear"
          format="currency"
          tooltipContent={historicalTooltipContent}
        />
        <SummaryTile
          title={`${thisYearSales.year} Total ~`}
          current={thisYearEstimates.totals.sales}
          timespan="thisYearEstimates"
          format="currency"
        />
      </div>
    </div>
  );
};
